import { computed, ref, watch } from "vue";
import type { Ref } from "vue";
import { useQueryClient } from "@tanstack/vue-query";
import { useTrpc } from "@app/vue/providers/TrpcProvider";

export interface ReservationCountParams {
  /** UTC ISO string — start of the window to count confirmed reservations. */
  confirmedDateFrom: Ref<string | undefined>;
  /** UTC ISO string — end of the window to count confirmed reservations. */
  confirmedDateTo: Ref<string | undefined>;
  /** UTC ISO string — start of the window to count seated reservations. */
  seatedDateFrom: Ref<string | undefined>;
  /** UTC ISO string — end of the window to count seated reservations. */
  seatedDateTo: Ref<string | undefined>;
  /** UTC ISO string — optional start of the window to count pending requests. */
  pendingDateFrom?: Ref<string | undefined>;
  /** UTC ISO string — optional end of the window to count pending requests. */
  pendingDateTo?: Ref<string | undefined>;
}

export const reservationCountKeys = {
  all: ["reservationCounts"] as const,
  counts: (params: {
    confirmedDateFrom?: string;
    confirmedDateTo?: string;
    seatedDateFrom?: string;
    seatedDateTo?: string;
    pendingDateFrom?: string;
    pendingDateTo?: string;
  }) => ["reservationCounts", params] as const,
};

/**
 * Fetches confirmed / seated / pending counts + people sums via a single
 * lightweight SQL query (no full rows loaded).
 *
 * - confirmed: bounded by confirmedDateFrom/To
 * - seated:    bounded by seatedDateFrom/To
 * - pending:   optionally bounded by pendingDateFrom/To
 */
export function useReservationCounts(params: ReservationCountParams) {
  const trpc = useTrpc();
  const queryClient = useQueryClient();
  const counts = ref({
    confirmed: 0,
    confirmedPeople: 0,
    seated: 0,
    seatedPeople: 0,
    pending: 0,
    pendingPeople: 0,
  });
  const isLoading = ref(false);

  const refresh = async () => {
    isLoading.value = true;
    try {
      const result = await trpc.reservations.getCounts.query({
        confirmed_date_from: params.confirmedDateFrom.value,
        confirmed_date_to: params.confirmedDateTo.value,
        seated_date_from: params.seatedDateFrom.value,
        seated_date_to: params.seatedDateTo.value,
        pending_date_from: params.pendingDateFrom?.value,
        pending_date_to: params.pendingDateTo?.value,
      });
      counts.value = {
        confirmed: result.confirmed ?? 0,
        confirmedPeople: result.confirmedPeople ?? 0,
        seated: result.seated ?? 0,
        seatedPeople: result.seatedPeople ?? 0,
        pending: result.pending ?? 0,
        pendingPeople: result.pendingPeople ?? 0,
      };
    } catch (error) {
      console.error("Failed to fetch reservation counts:", error);
    } finally {
      isLoading.value = false;
    }
  };

  watch(
    [
      params.confirmedDateFrom,
      params.confirmedDateTo,
      params.seatedDateFrom,
      params.seatedDateTo,
      ...(params.pendingDateFrom ? [params.pendingDateFrom] : []),
      ...(params.pendingDateTo ? [params.pendingDateTo] : []),
    ],
    () => {
      void refresh();
    },
    { immediate: true },
  );

  return {
    confirmed: computed(() =>
      isLoading.value ? null : counts.value.confirmed,
    ),
    confirmedPeople: computed(() =>
      isLoading.value ? null : counts.value.confirmedPeople,
    ),
    seated: computed(() => (isLoading.value ? null : counts.value.seated)),
    seatedPeople: computed(() =>
      isLoading.value ? null : counts.value.seatedPeople,
    ),
    pending: computed(() => (isLoading.value ? null : counts.value.pending)),
    pendingPeople: computed(() =>
      isLoading.value ? null : counts.value.pendingPeople,
    ),
    isLoading,
    refresh,
  };
}
